import React, { useEffect, useRef, useContext, useState, useCallback } from 'react';
import { CheckOutContext } from './CheckOutContext';
import { DeleteCartItems, GetCartItems, SaveItems, GetSimilarProducts, GetBoughtItems, GetSavedItems, GetPersonalizedRecommendations, GetComplementaryProducts, GetFrequentlyBought, GetCartTotal, MoveSavedToCart, MoveCartToSaved, DeleteSavedItem } from '../Data.js';
import Dialogs from "./Dialogs.js";
import CompareSimilarItems from "./Comparison.js";
import { cartContext } from "./CartContext";
import { Link, useNavigate } from 'react-router-dom';
import RecentlyViewed from "./RecentlyViewed";
import { FaSpinner, FaShoppingCart, FaHeart, FaShareAlt, FaTrash, FaArrowLeft, FaArrowRight, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const ProductPage = () => {
  const { checkOutData, addItemTocheckOut, removeItemFromCheckout, subTotal, updateItemQuantity, getItemCount, isItemInCheckout } = useContext(CheckOutContext);
  const [products, setProducts] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [boughtProducts, setBoughtProducts] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [personalizedItems, setPersonalizedItems] = useState([]);
  const [complementaryItems, setComplementaryItems] = useState([]);
  const [freqBoughtItems, setFreqBoughtItems] = useState([]);
  const [comparisonProductID, setComparisonProduct] = useState("");
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [cartID, setCartID] = useState(0);
  const { cartCount, updateCartCount } = useContext(cartContext);
  const [showSuccessDialog, setSuccessDialog] = useState(false);
  const [showErrorDialog, setErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavedLoading, setIsSavedLoading] = useState(true);
  const [isBoughtLoading, setIsBoughtLoading] = useState(true);
  const [userId, setUserId] = useState(null);

    // New state for toggling sections
  const [showAllSaved, setShowAllSaved] = useState(false);
  const [showAllBought, setShowAllBought] = useState(false);
  const [showAllComplementary, setShowAllComplementary] = useState(false);
  const [showAllPersonalized, setShowAllPersonalized] = useState(false);
  const [showAllFreqBought, setShowAllFreqBought] = useState(false);

  // Ref for sticky checkout button
  const checkoutButtonRef = useRef(null);


  // Refs for carousels
  const savedItemsRef = useRef(null);
  const complementaryRef = useRef(null);
  const personalizedRef = useRef(null);
  const relatedRef = useRef(null);
  const buyAgainRef = useRef(null);
  const scrollRef = useRef(null);

  const navigate = useNavigate();

  // Helper function to increment quantity
  const incrementQuantity = (index, product) => {
    const maxQuantity = Math.min(product.stockQuantity || 99, 99);
    if (product.quantity < maxQuantity) {
      const newQuantity = product.quantity + 1;
      
      // Update local state
      setProducts(products.map((p, i) =>
        i === index ? { ...p, quantity: newQuantity } : p
      ));
      
      // Update checkout context
      updateItemQuantity(product.productId, newQuantity);
    }
  };

  // Helper function to decrement quantity
  const decrementQuantity = (index, product) => {
    if (product.quantity > 1) {
      const newQuantity = product.quantity - 1;
      
      // Update local state
      setProducts(products.map((p, i) =>
        i === index ? { ...p, quantity: newQuantity } : p
      ));
      
      // Update checkout context
      updateItemQuantity(product.productId, newQuantity);
    }
  };

  // Helper function to get product image URL from either JSON array or direct URL
  const getProductImageUrl = (productImage) => {
    try {
      // Try parsing as JSON first (legacy format)
      const productImages = JSON.parse(productImage);
      return Array.isArray(productImages) ? productImages[0] : productImage;
    } catch {
      // If parsing fails, it's already a direct URL (new format)
      return productImage;
    }
  };

  
  // Sticky checkout button effect
  useEffect(() => {
    const handleScroll = () => {
      if (checkoutButtonRef.current) {
        const scrollPosition = window.scrollY;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // Show sticky button when scrolled past the original checkout section
        if (scrollPosition > 200 && scrollPosition < documentHeight - windowHeight - 100) {
          checkoutButtonRef.current.classList.remove('hidden');
        } else {
          checkoutButtonRef.current.classList.add('hidden');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setUserId(localStorage.getItem("userID"));
  }, []);

  // Fetch cart items
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const userId = localStorage.getItem('userID');
        
        if (!userId) {
          // Guest user: load from checkOutContext (which syncs with localStorage)
          console.log('Guest user detected. Loading from checkOutContext:', checkOutData);
          const guestCart = checkOutData.map(item => ({
            productId: item.productID,
            productName: item.productName,
            price: item.price,
            quantity: item.quantity,
            productImage: item.productImage,
            stockQuantity: item.stockQuantity || 99,
            inStock: item.inStock !== false
          }));
          console.log('Guest cart products:', guestCart);
          setProducts(guestCart);
          setIsLoading(false);
          return;
        }
        
        const response = await GetCartItems(userId);
        if (response && response.success && response.data) {
          const serverProducts = response.data.items || [];
          
          // Sync quantities with CheckOutContext
          const updatedProducts = serverProducts.map(serverProduct => {
            const checkoutItem = checkOutData.find(item => 
              item.productID === serverProduct.productId
            );
            return {
              ...serverProduct,
              quantity: checkoutItem ? checkoutItem.quantity : serverProduct.quantity
            };
          });
          
          setProducts(updatedProducts);
          setCartID(response.data.cartId || 0);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [cartCount, checkOutData]);

  // Fetch saved items
  useEffect(() => {
    const fetchSavedItems = async () => {
      try {
        setIsSavedLoading(true);
        const userId = localStorage.getItem("userID");
        if (!userId) {
          setSavedProducts([]);
          return;
        }
        
        const response = await GetSavedItems(userId);
        if (response && response.success && response.data) {
          setSavedProducts(response.data || []);
        } else {
          setSavedProducts([]);
        }
      } catch (error) {
        console.error("Error fetching saved items:", error);
        setSavedProducts([]);
      } finally {
        setIsSavedLoading(false);
      }
    };

    fetchSavedItems();
  }, []);

  // Fetch personalized recommendations
  useEffect(() => {
    const fetchPersonalizedItems = async () => {
      try {
        if (userId) {
          const response = await GetPersonalizedRecommendations(userId);
          if (response && response.success && response.data) {
            setPersonalizedItems(response.data || []);
          } else {
            setPersonalizedItems([]);
          }
        }
      } catch (error) {
        console.error("Error fetching Personalized items:", error);
        setPersonalizedItems([]);
      }
    };

    fetchPersonalizedItems();
  }, [userId]);

  // Fetch bought items
  useEffect(() => {
    const fetchBoughtItems = async () => {
      try {
        if (userId) {
          const response = await GetBoughtItems(userId);
          if (response && response.success && response.data) {
          setBoughtProducts(response.data || []);
          }
        } else {
          setBoughtProducts([]);
        }
      } catch (error) {
        console.error("Error fetching bought items:", error);
      } finally {
        setIsBoughtLoading(false);
      }
    };

    fetchBoughtItems();
  }, []);

  // Carousel scroll functions
  const scrollLeft = (ref) => {
    ref.current?.scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollRight = (ref) => {
    ref.current?.scrollBy({ left: 300, behavior: 'smooth' });
  };

  // Improved checkout handler with better validation
  const handleChecking = useCallback(async (product) => {
    const productIdToUse = product.productId;
    
    if (!productIdToUse) {
      console.warn('Product ID is missing:', product);
      return;
    }

    const isAlreadySelected = isItemInCheckout(productIdToUse);
    
    if (isAlreadySelected) {
      removeItemFromCheckout(productIdToUse);
    } else {
      // Validate required fields before adding
      if (!product.productName || !product.price || !product.quantity) {
        console.warn('Missing required product fields:', product);
        return;
      }
      
      addItemTocheckOut({
        productID: productIdToUse,
        productName: product.productName,
        productImage: product.productImage,
        price: Number(product.price),
        quantity: Number(product.quantity),
        merchantId: product.merchantId
      });
    }

    // Get complementary products when a product is selected (only when adding)
    if (!isAlreadySelected) {
      try {
        const [complementaryProducts, freqBoughtTogether] = await Promise.all([
          GetComplementaryProducts(productIdToUse),
          GetFrequentlyBought(productIdToUse)
        ]);

        if (complementaryProducts && complementaryProducts.success && complementaryProducts.data) {
          setComplementaryItems(complementaryProducts.data || []);
        } else {
          setComplementaryItems([]);
        }

        if (freqBoughtTogether && freqBoughtTogether.success && freqBoughtTogether.data) {
          setFreqBoughtItems(freqBoughtTogether.data || []);
        } else {
          setFreqBoughtItems([]);
        }
      } catch (error) {
        console.error("Error fetching recommendations:", error);
        setComplementaryItems([]);
        setFreqBoughtItems([]);
      }
    }
  }, [isItemInCheckout, removeItemFromCheckout, addItemTocheckOut]);

  const DeselectItems = useCallback(() => {
    checkOutData.forEach(item => removeItemFromCheckout(item.productID));
  }, [checkOutData, removeItemFromCheckout]);

  const handleCheckout = useCallback(() => {
    if (checkOutData.length > 0) {
      navigate("/MainCheckout");
    } else {
      alert('Please select items for checkout.');
    }
  }, [checkOutData.length, navigate]);

  const HandleDeleteCartItems = async (cartItemID, productID) => {
    try {
      const requestData = { cartID, cartItemID, productID };
      const response = await DeleteCartItems(requestData);
      if (response?.responseMessage) {
        updateCartCount(parseInt(cartCount) - 1);
        setSuccessDialog(true);
        setDialogMessage("Product Removed From Cart Successfully");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const HandleSaveItems = async (productID, quantity) => {
    try {
      const userId = localStorage.getItem('userID');
      if (!userId) {
        console.error('User not logged in');
        return;
      }
      
      const requestData = {
        applicationUserId: userId,
        productId: productID,
        quantity: quantity || 1
      };
      
      const response = await SaveItems(requestData);
      if (response && response.success) {
        // Refresh saved items list
        const savedResponse = await GetSavedItems(userId);
        if (savedResponse && savedResponse.success && savedResponse.data) {
          setSavedProducts(savedResponse.data || []);
        }
        setSuccessDialog(true);
        setDialogMessage('Item saved successfully!');
      } else {
        setErrorDialog(true);
        setDialogMessage(response?.errors?.join(', ') || 'Failed to save item');
      }
    } catch (error) {
      console.error('Error saving item:', error);
      setErrorDialog(true);
      setDialogMessage('Failed to save item');
    }
  };

  // Move saved item to cart
  const HandleMoveToCart = async (productId, quantity = 1) => {
    try {
      const userId = localStorage.getItem('userID');
      if (!userId) {
        console.error('User not logged in');
        return;
      }
      
      const requestData = {
        applicationUserId: userId,
        productId: productId,
        quantity: quantity
      };
      
      const response = await MoveSavedToCart(requestData);
      if (response && response.success) {
        // Refresh both saved items and cart
        const savedResponse = await GetSavedItems(userId);
        if (savedResponse && savedResponse.success && savedResponse.data) {
          setSavedProducts(savedResponse.data || []);
        }
        
        // Refresh cart items
        const cartResponse = await GetCartItems(userId);
        if (cartResponse && cartResponse.success && cartResponse.data) {
          setProducts(cartResponse.data.items || []);
        }
        
        setSuccessDialog(true);
        setDialogMessage('Item moved to cart successfully!');
      } else {
        setErrorDialog(true);
        setDialogMessage(response?.errors?.join(', ') || 'Failed to move item to cart');
      }
    } catch (error) {
      console.error('Error moving item to cart:', error);
      setErrorDialog(true);
      setDialogMessage('Failed to move item to cart');
    }
  };

  // Move cart item to saved
  const HandleMoveToSaved = async (productId, quantity = 1) => {
    try {
      const userId = localStorage.getItem('userID');
      if (!userId) {
        console.error('User not logged in');
        return;
      }
      
      const requestData = {
        applicationUserId: userId,
        productId: productId,
        quantity: quantity
      };
      
      const response = await MoveCartToSaved(requestData);
      if (response && response.success) {
        // Refresh both saved items and cart
        const savedResponse = await GetSavedItems(userId);
        if (savedResponse && savedResponse.success && savedResponse.data) {
          setSavedProducts(savedResponse.data || []);
        }
        
        // Refresh cart items
        const cartResponse = await GetCartItems(userId);
        if (cartResponse && cartResponse.success && cartResponse.data) {
          setProducts(cartResponse.data.items || []);
        }
        
        setSuccessDialog(true);
        setDialogMessage('Item moved to saved items successfully!');
      } else {
        setErrorDialog(true);
        setDialogMessage(response?.errors?.join(', ') || 'Failed to move item to saved');
      }
    } catch (error) {
      console.error('Error moving item to saved:', error);
      setErrorDialog(true);
      setDialogMessage('Failed to move item to saved');
    }
  };

  // Delete saved item
  const HandleDeleteSavedItem = async (productId) => {
    try {
      const userId = localStorage.getItem('userID');
      if (!userId) {
        console.error('User not logged in');
        return;
      }
      
      const response = await DeleteSavedItem(productId, userId);
      if (response && response.success) {
        // Refresh saved items list
        const savedResponse = await GetSavedItems(userId);
        if (savedResponse && savedResponse.success && savedResponse.data) {
          setSavedProducts(savedResponse.data || []);
        }
        setSuccessDialog(true);
        setDialogMessage('Saved item removed successfully!');
      } else {
        setErrorDialog(true);
        setDialogMessage(response?.errors?.join(', ') || 'Failed to remove saved item');
      }
    } catch (error) {
      console.error('Error removing saved item:', error);
      setErrorDialog(true);
      setDialogMessage('Failed to remove saved item');
    }
  };

  const handleCloseDialog = () => {
    setSuccessDialog(false);
    setErrorDialog(false);
    setDialogMessage(null);
  };

  const handleOpenModal = async (e, productID) => {
    e.preventDefault();
    
    if (!productID) {
      console.error('ProductID is undefined or null');
      setErrorDialog(true);
      setDialogMessage("Product ID is missing. Cannot compare similar items.");
      return;
    }
    
    try {
      const similarProductsResponse = await GetSimilarProducts(productID, 5);
      setSimilarProducts(similarProductsResponse);
      setComparisonProduct(productID);
      setShowCompareModal(true);
    } catch (error) {
      console.error("Error fetching similar products:", error);
      setErrorDialog(true);
      setDialogMessage("Failed to load similar products");
    }
  };

  const handleCloseModal = () => {
    setShowCompareModal(false);
  };

  
  // Modified ProductCard component with responsive sizing
  const ProductCard = ({ product, showDiscount = true, onMoveToCart, onDelete, showMoveToCart = false, showDelete = false }) => {
    const discount = product.discount ? product.discount / 100 : 0;
    const discountedPrice = discount > 0 
      ? (product.price * (1 - discount)).toFixed(2)
      : product.price;
    const firstImage = getProductImageUrl(product.productImage);
    const hasDiscount = discount > 0 && showDiscount;
  
    return (
      <div className="w-full sm:w-48 p-2 sm:p-3 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow relative">
        {hasDiscount && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded z-10">
            -{(discount * 100).toFixed(0)}%
          </div>
        )}
        <Link to={`/product/${encodeURIComponent(product.productName)}/${product.productId || product.productID}`}>
          <img 
            src={firstImage} 
            alt={product.productName}
            className="w-full h-32 sm:h-40 object-contain" 
            onError={(e) => {
              e.target.src = '/api/placeholder/300/300';
            }}
          />
          <div className="mt-2 sm:mt-3 space-y-1">
            <h3 className="text-xs sm:text-sm font-medium hover:underline line-clamp-2">{product.productName}</h3>
            <div className={`text-xs ${product.inStock ? 'text-green-600' : 'text-red-600'}`}>
              {product.inStock ? "In Stock" : "Out of Stock"}
            </div>
            <div className="flex items-center text-xs text-yellow-500">
              ⭐{4.5} ({Math.floor(Math.random() * 10000)})
            </div>
            {hasDiscount && (
              <div className="text-xs text-gray-500 line-through">
                Was KSH {Number(product.price).toLocaleString()}
              </div>
            )}
            <div className="text-sm sm:text-base font-bold text-gray-800">
              KSH {Number(discountedPrice).toLocaleString()}
            </div>
          </div>
        </Link>
        
        {/* Action buttons for saved items */}
        {(showMoveToCart || showDelete) && (
          <div className="mt-2 flex gap-2">
            {showMoveToCart && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  onMoveToCart && onMoveToCart(product.productId || product.productID, product.quantity);
                }}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium py-1 px-2 rounded"
              >
                Move to Cart
              </button>
            )}
            {showDelete && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  onDelete && onDelete(product.productId || product.productID);
                }}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium py-1 px-2 rounded"
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
    );
  };


  const RecommendationSection = ({ 
  title, 
  items, 
  isLoading, 
  showAll, 
  setShowAll,
  showDiscount = true,
  onMoveToCart,
  onDelete,
  showSavedActions = false
}) => {
  if (!items || items.length === 0) return null;

  
  
  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 300, behavior: 'smooth' });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
      <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">{title}</h2>
      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <FaSpinner className="animate-spin text-yellow-500 text-2xl" />
        </div>
      ) : (
        <>
          {/* Desktop View - Hidden on mobile */}
          <div className="hidden md:block relative group">
            <button 
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white text-yellow-600 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg hover:bg-yellow-50"
            >
              <FaArrowLeft className="h-4 w-4" />
            </button>
            
            <div 
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto py-2 scroll-smooth snap-x snap-mandatory no-scrollbar"
            >
              {items.map((product, index) => (
                <div key={index} className="flex-shrink-0 w-52">
                  <ProductCard 
                    product={product} 
                    showDiscount={showDiscount}
                    showMoveToCart={showSavedActions}
                    showDelete={showSavedActions}
                    onMoveToCart={onMoveToCart}
                    onDelete={onDelete}
                  />
                </div>
              ))}
            </div>
            
            <button 
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white text-yellow-600 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg hover:bg-yellow-50"
            >
              <FaArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Mobile View - Hidden on desktop */}
          <div className="md:hidden grid grid-cols-2 gap-3">
            {items.slice(0, showAll ? items.length : 4).map((product, index) => (
              <ProductCard 
                key={index} 
                product={product} 
                showDiscount={showDiscount}
                showMoveToCart={showSavedActions}
                showDelete={showSavedActions}
                onMoveToCart={onMoveToCart}
                onDelete={onDelete}
              />
            ))}
          </div>

          {items.length > 4 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-4 text-yellow-600 hover:text-yellow-700 flex items-center text-sm md:hidden"
            >
              {showAll ? (
                <>
                  <span>Show Less</span>
                  <FaChevronUp className="ml-1" />
                </>
              ) : (
                <>
                  <span>Show More</span>
                  <FaChevronDown className="ml-1" />
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
};
   // Helper component for recommendation sections
  // const RecommendationSection = ({ 
  //   title, 
  //   items, 
  //   isLoading, 
  //   showAll, 
  //   setShowAll,
  //   showDiscount = true 
  // }) => {
  //   if (!items || items.length === 0) return null;

  //   const visibleItems = showAll ? items : items.slice(0, 4);
  //   const isMobile = window.innerWidth < 640;

  //   return (
  //     <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
  //       <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">{title}</h2>
  //       {isLoading ? (
  //         <div className="flex justify-center items-center h-32">
  //           <FaSpinner className="animate-spin text-yellow-500 text-2xl" />
  //         </div>
  //       ) : (
  //         <>
  //           {isMobile ? (
  //             <div className="grid grid-cols-2 gap-3">
  //               {visibleItems.map((product, index) => (
  //                 <ProductCard key={index} product={product} showDiscount={showDiscount} />
  //               ))}
  //             </div>
  //           ) : (
  //             <div className="relative">
  //               <button 
  //                 onClick={() => scrollLeft(relatedRef)}
  //                 className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-md z-10 hover:bg-yellow-50"
  //               >
  //                 <FaArrowLeft className="text-yellow-600" />
  //               </button>
  //               <div 
  //                 className="flex gap-4 overflow-x-auto py-2 scrollbar-hide"
  //               >
  //                 {items.map((product, index) => (
  //                   <ProductCard key={index} product={product} showDiscount={showDiscount} />
  //                 ))}
  //               </div>
  //               <button 
  //                 onClick={() => scrollRight(relatedRef)}
  //                 className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-md z-10 hover:bg-yellow-50"
  //               >
  //                 <FaArrowRight className="text-yellow-600" />
  //               </button>
  //             </div>
  //           )}
            
  //           {items.length > 4 && isMobile && (
  //             <button
  //               onClick={() => setShowAll(!showAll)}
  //               className="mt-4 text-yellow-600 hover:text-yellow-700 flex items-center text-sm"
  //             >
  //               {showAll ? (
  //                 <>
  //                   <span>Show Less</span>
  //                   <FaChevronUp className="ml-1" />
  //                 </>
  //               ) : (
  //                 <>
  //                   <span>Show More</span>
  //                   <FaChevronDown className="ml-1" />
  //                 </>
  //               )}
  //             </button>
  //           )}
  //         </>
  //       )}
  //     </div>
  //   );
  // };

  // return (
  //   <div className="bg-gray-50 min-h-screen p-4 md:p-8">
  //     {/* Dialogs */}
  //     {showSuccessDialog && <Dialogs message={dialogMessage} type="cart" onClose={handleCloseDialog} />}
  //     {showErrorDialog && <Dialogs message={dialogMessage} type="error" onClose={handleCloseDialog} />}

  //     <div className="flex flex-col lg:flex-row gap-6">
  //       {/* Main Content */}
  //       <div className="flex-1">
  //         {/* Shopping Cart Section */}
  //         <div className="bg-white rounded-lg shadow-md p-6 mb-6">
  //           <div className="flex justify-between items-center mb-4">
  //             <h1 className="text-2xl font-bold text-gray-800">Shopping Cart</h1>
  //             {products.length > 0 && (
  //               <button 
  //                 onClick={DeselectItems}
  //                 className="text-yellow-600 hover:text-yellow-700 text-sm"
  //               >
  //                 Deselect all items
  //               </button>
  //             )}
  //           </div>

  //           {isLoading ? (
  //             <div className="flex justify-center items-center h-40">
  //               <FaSpinner className="animate-spin text-yellow-500 text-4xl" />
  //             </div>
  //           ) : products.length > 0 ? (
  //             products.map((product, index) => (
  //               <div key={index} className="flex flex-col md:flex-row gap-4 py-4 border-b border-gray-200">
  //                 <div className="flex items-start">
  //                   <input
  //                     type="checkbox"
  //                     checked={checkOutData.some(item => item.productID === product.productID)}
  //                     onChange={() => handleChecking(product)}
  //                     className="h-5 w-5 text-yellow-600 rounded focus:ring-yellow-500"
  //                   />
  //                 </div>
  //                 <div className="flex-1 flex flex-col md:flex-row gap-4">
  //                   <img 
  //                     src={JSON.parse(product.productImage)[0]} 
  //                     alt={product.productName}
  //                     className="w-32 h-32 object-contain bg-white p-2 border border-gray-200 rounded"
  //                   />
  //                   <div className="flex-1">
  //                     <h3 className="text-lg font-medium text-gray-800">{product.productName}</h3>
  //                     <p className={`text-sm ${product.inStock ? 'text-green-600' : 'text-red-600'}`}>
  //                       {product.inStock ? "In Stock" : "Only a few left in stock - order soon."}
  //                     </p>
  //                     <div className="flex items-center mt-2">
  //                       <label className="mr-2 text-sm">Qty:</label>
  //                       <select
  //                         value={product.quantity}
  //                         onChange={(e) => {
  //                           const newQuantity = parseInt(e.target.value);
  //                           setProducts(products.map((p, i) =>
  //                             i === index ? { ...p, quantity: newQuantity } : p
  //                           ));
  //                         }}
  //                         className="border border-gray-300 rounded px-2 py-1 text-sm"
  //                       >
  //                         {[...Array(10).keys()].map(n => (
  //                           <option key={n} value={n + 1}>{n + 1}</option>
  //                         ))}
  //                       </select>
  //                     </div>
  //                     <div className="flex flex-wrap gap-3 mt-3">
  //                       <button 
  //                         onClick={(e) => { e.preventDefault(); HandleDeleteCartItems(product.cartItemID, product.productID); }}
  //                         className="flex items-center text-sm text-gray-600 hover:text-yellow-600"
  //                       >
  //                         <FaTrash className="mr-1" /> Remove
  //                       </button>
  //                       <button 
  //                         onClick={(e) => { e.preventDefault(); HandleSaveItems(product.productID, product.quantity); }}
  //                         className="flex items-center text-sm text-gray-600 hover:text-yellow-600"
  //                       >
  //                         <FaHeart className="mr-1" /> Save for later
  //                       </button>
  //                       <button 
  //                         onClick={(e) => handleOpenModal(e, product.productID)}
  //                         className="flex items-center text-sm text-gray-600 hover:text-yellow-600"
  //                       >
  //                         Compare with similar items
  //                       </button>
  //                       <button className="flex items-center text-sm text-gray-600 hover:text-yellow-600">
  //                         <FaShareAlt className="mr-1" /> Share
  //                       </button>
  //                     </div>
  //                   </div>
  //                   <div className="text-lg font-bold">KSH{product.price.toFixed(2)}</div>
  //                 </div>
  //               </div>
  //             ))
  //           ) : (
  //             <div className="flex flex-col items-center justify-center py-12">
  //               <img src="../Images/11329060.png" alt="Empty Cart" className="w-40 mb-6" />
  //               <h2 className="text-xl font-bold text-gray-800 mb-2">Your Cart is Empty</h2>
  //               <p className="text-gray-600 mb-6">Looks like you haven't added anything to your cart yet.</p>
  //               <button 
  //                 onClick={() => navigate('/')} 
  //                 className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-6 rounded-lg"
  //               >
  //                 Start Shopping
  //               </button>
  //             </div>
  //           )}
  //         </div>

        

        
  //     {/* Sticky Checkout Button (mobile only) */}
  //     {checkOutData.length > 0 && (
  //       <div 
  //         ref={checkoutButtonRef}
  //         className="fixed hidden bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-4 z-50 md:hidden"
  //       >
  //         <div className="container mx-auto flex justify-between items-center">
  //           <div>
  //             <p className="text-sm font-medium">
  //               {checkOutData.length} items: <span className="font-bold">KSH{subTotal.toFixed(2)}</span>
  //             </p>
  //           </div>
  //           <button 
  //             onClick={handleCheckout}
  //             className="bg-yellow-500 hover:bg-yellow-600 text-gray-800 font-bold py-2 px-4 rounded-lg text-sm"
  //           >
  //             Proceed to checkout
  //           </button>
  //         </div>
  //       </div>
  //     )}

  //       {/* Sidebar */}
  //       <div className="lg:w-80 space-y-6">
  //         {/* Checkout Summary */}
  //         <div className="bg-white rounded-lg shadow-md p-6">
  //           <div className="mb-4">
  //             <p className="text-lg font-medium">
  //               Subtotal ({checkOutData.length} items): <span className="font-bold">KSH{subTotal.toFixed(2)}</span>
  //             </p>
  //           </div>
  //           <button 
  //             onClick={handleCheckout} 
  //             disabled={checkOutData.length === 0}
  //             className={`w-full py-3 rounded-lg font-bold ${checkOutData.length > 0 ? 'bg-yellow-500 hover:bg-yellow-600 text-gray-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
  //           >
  //             Proceed to checkout
  //           </button>
  //         </div>

  //         {/* Recently Viewed */}
  //         {/* <div className="bg-white rounded-lg shadow-md p-6">
  //           <h2 className="text-xl font-bold text-gray-800 mb-4">Recently Viewed</h2>
  //           <RecentlyViewed />
  //         </div> */}
  //       </div>
  //     </div>

  //       {/* Modified Recommendations Sections */}
  //     <div className="mt-8 space-y-4 sm:space-y-6">
  //       {/* Saved Items */}
  //       <RecommendationSection
  //         title="Your Saved Items"
  //         items={savedProducts}
  //         isLoading={isSavedLoading}
  //         showAll={showAllSaved}
  //         setShowAll={setShowAllSaved}
  //       />

  //       {/* Buy Again */}
  //       <RecommendationSection
  //         title="Buy Again"
  //         items={boughtProducts}
  //         isLoading={isBoughtLoading}
  //         showAll={showAllBought}
  //         setShowAll={setShowAllBought}
  //         showDiscount={false}
  //       />

  //       {/* Complementary Products */}
  //       <RecommendationSection
  //         title="Complement your products for a better experience"
  //         items={complementaryItems}
  //         isLoading={false}
  //         showAll={showAllComplementary}
  //         setShowAll={setShowAllComplementary}
  //       />

  //       {/* Personalized Recommendations */}
  //       <RecommendationSection
  //         title="Personalized based on your shopping trends"
  //         items={personalizedItems}
  //         isLoading={false}
  //         showAll={showAllPersonalized}
  //         setShowAll={setShowAllPersonalized}
  //       />

  //       {/* Frequently Bought Together */}
  //       <RecommendationSection
  //         title="Customers who bought this also bought"
  //         items={freqBoughtItems}
  //         isLoading={false}
  //         showAll={showAllFreqBought}
  //         setShowAll={setShowAllFreqBought}
  //       />
  //     </div>
        
  //     </div> 

  //     {/* Comparison Modal */}
  //     {showCompareModal && (
  //       <CompareSimilarItems
  //         product={products.find(p => p.productID === comparisonProductID)}
  //         similarProducts={similarProducts}
  //         onClose={handleCloseModal}
  //       />
  //     )}
  //   </div>
  // );

  return (
  <div className="bg-gray-50 min-h-screen p-4 md:p-8">
    {/* Dialogs */}
    {showSuccessDialog && <Dialogs message={dialogMessage} type="cart" onClose={handleCloseDialog} />}
    {showErrorDialog && <Dialogs message={dialogMessage} type="error" onClose={handleCloseDialog} />}

    {/* Main Content - Cart and Checkout Side by Side on Desktop */}
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Cart Items Section */}
      <div className="flex-1 bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Shopping Cart</h1>
          {products.length > 0 && (
            <button 
              onClick={DeselectItems}
              className="text-yellow-600 hover:text-yellow-700 text-sm"
            >
              Deselect all items
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <FaSpinner className="animate-spin text-yellow-500 text-4xl" />
          </div>
        ) : products.length > 0 ? (
          products.map((product, index) => (
            <div key={index} className="flex flex-col md:flex-row gap-4 py-4 border-b border-gray-200">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  checked={isItemInCheckout(product.productId)}
                  onChange={() => handleChecking(product)}
                  className="h-5 w-5 text-yellow-600 rounded focus:ring-yellow-500"
                />
              </div>
              <div className="flex-1 flex flex-col md:flex-row gap-4">
                <img 
                  src={getProductImageUrl(product.productImage)} 
                  alt={product.productName}
                  className="w-32 h-32 object-contain bg-white p-2 border border-gray-200 rounded"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-800">{product.productName}</h3>
                  <p className={`text-sm ${product.inStock ? 'text-green-600' : 'text-red-600'}`}>
                    {product.inStock ? "In Stock" : "Only a few left in stock - order soon."}
                  </p>
                  <div className="flex items-center mt-2">
                    <label className="mr-3 text-sm font-medium">Qty:</label>
                    <div className="flex items-center justify-center border border-gray-300 rounded-md overflow-hidden bg-white">
                      <button
                        onClick={() => decrementQuantity(index, product)}
                        disabled={product.quantity <= 1}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed text-gray-700 font-medium transition-colors duration-200 flex-shrink-0 text-sm"
                      >
                        −
                      </button>
                      <span className="px-3 py-1 bg-white text-center font-medium text-gray-900 min-w-[3rem] border-l border-r border-gray-300 text-sm">
                        {product.quantity}
                      </span>
                      <button
                        onClick={() => incrementQuantity(index, product)}
                        disabled={product.quantity >= Math.min(product.stockQuantity || 99, 99)}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed text-gray-700 font-medium transition-colors duration-200 flex-shrink-0 text-sm"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3">
                    <button 
                      onClick={(e) => { e.preventDefault(); HandleDeleteCartItems(product.cartItemId, product.productId); }}
                      className="flex items-center text-sm text-gray-600 hover:text-yellow-600"
                    >
                      <FaTrash className="mr-1" /> Remove
                    </button>
                    <button 
                      onClick={(e) => { e.preventDefault(); HandleSaveItems(product.productId, product.quantity); }}
                      className="flex items-center text-sm text-gray-600 hover:text-yellow-600"
                    >
                      <FaHeart className="mr-1" /> Save for later
                    </button>
                    <button 
                      onClick={(e) => handleOpenModal(e, product.productId)}
                      className="flex items-center text-sm text-gray-600 hover:text-yellow-600"
                    >
                      Compare with similar items
                    </button>
                    <button className="flex items-center text-sm text-gray-600 hover:text-yellow-600">
                      <FaShareAlt className="mr-1" /> Share
                    </button>
                  </div>
                </div>
                <div className="text-lg font-bold">KSH{product.price.toFixed(2)}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <img src="../Images/11329060.png" alt="Empty Cart" className="w-40 mb-6" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Your Cart is Empty</h2>
            <p className="text-gray-600 mb-6">Looks like you haven't added anything to your cart yet.</p>
            <button 
              onClick={() => navigate('/')} 
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-6 rounded-lg"
            >
              Start Shopping
            </button>
          </div>
        )}
      </div>

      {/* Checkout Section - On the same row as Cart on Desktop */}
      <div className="lg:w-80">
        <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
          <div className="mb-4">
            <p className="text-lg font-medium">
              Subtotal ({checkOutData.length} items): <span className="font-bold">KSH{subTotal.toFixed(2)}</span>
            </p>
          </div>
          <button 
            onClick={handleCheckout} 
            disabled={checkOutData.length === 0}
            className={`w-full py-3 rounded-lg font-bold ${checkOutData.length > 0 ? 'bg-yellow-500 hover:bg-yellow-600 text-gray-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
          >
            Proceed to checkout
          </button>
        </div>
      </div>
    </div>

    {/* Mobile Sticky Checkout Button */}
    {checkOutData.length > 0 && (
      <div 
        ref={checkoutButtonRef}
        className="fixed hidden bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-4 z-50 md:hidden"
      >
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <p className="text-sm font-medium">
              {checkOutData.length} items: <span className="font-bold">KSH{subTotal.toFixed(2)}</span>
            </p>
          </div>
          <button 
            onClick={handleCheckout}
            className="bg-yellow-500 hover:bg-yellow-600 text-gray-800 font-bold py-2 px-4 rounded-lg text-sm"
          >
            Proceed to checkout
          </button>
        </div>
      </div>
    )}

    {/* Recommendation Sections Below */}
    <div className="mt-8 space-y-4 sm:space-y-6">
      {/* Saved Items */}
      <RecommendationSection
        title="Your Saved Items"
        items={savedProducts.map(item => ({...item, showMoveToCart: true, showDelete: true}))}
        isLoading={isSavedLoading}
        showAll={showAllSaved}
        setShowAll={setShowAllSaved}
        onMoveToCart={HandleMoveToCart}
        onDelete={HandleDeleteSavedItem}
        showSavedActions={true}
      />

      {/* Buy Again */}
      <RecommendationSection
        title="Buy Again"
        items={boughtProducts}
        isLoading={isBoughtLoading}
        showAll={showAllBought}
        setShowAll={setShowAllBought}
        showDiscount={false}
      />

      {/* Complementary Products */}
      <RecommendationSection
        title="Complement your products for a better experience"
        items={complementaryItems}
        isLoading={false}
        showAll={showAllComplementary}
        setShowAll={setShowAllComplementary}
      />

      {/* Personalized Recommendations */}
      <RecommendationSection
        title="Personalized based on your shopping trends"
        items={personalizedItems}
        isLoading={false}
        showAll={showAllPersonalized}
        setShowAll={setShowAllPersonalized}
      />

      {/* Frequently Bought Together */}
      <RecommendationSection
        title="Customers who bought this also bought"
        items={freqBoughtItems}
        isLoading={false}
        showAll={showAllFreqBought}
        setShowAll={setShowAllFreqBought}
      />
    </div>

    {/* Comparison Modal */}
    {showCompareModal && (
      <CompareSimilarItems
        product={products.find(p => p.productId === comparisonProductID)}
        similarProducts={similarProducts}
        onClose={handleCloseModal}
      />
    )}
  </div>
);
};

export default ProductPage;